export const REQUIRED_SCHEDULE_MODE_FIXED = 'fixed';
export const REQUIRED_SCHEDULE_MODE_PER_BLOCK = 'perBlock';

const DEFAULT_BLOCKS = [[0, 1, 2], [3, 4, 5]];

function normalizeRotationList(rotations) {
  return Array.from(new Set((Array.isArray(rotations) ? rotations : [])
    .map(Number)
    .filter(Number.isInteger)
    .filter((rotation) => rotation >= 0 && rotation < 6)))
    .sort((a, b) => a - b);
}

export function normalizeRequiredSchedule(schedule, fixedRotations = []) {
  const mode = schedule?.mode === REQUIRED_SCHEDULE_MODE_PER_BLOCK
    ? REQUIRED_SCHEDULE_MODE_PER_BLOCK
    : REQUIRED_SCHEDULE_MODE_FIXED;
  const rawBlocks = Array.isArray(schedule?.blocks) ? schedule.blocks : DEFAULT_BLOCKS;
  const blocks = rawBlocks
    .map(normalizeRotationList)
    .filter((block) => block.length > 0);
  const effectiveBlocks = blocks.length > 0 ? blocks : DEFAULT_BLOCKS.map((block) => block.slice());
  const shortestBlock = Math.min(...effectiveBlocks.map((block) => block.length));
  const allowedPositions = Array.from(new Set((Array.isArray(schedule?.allowedPositions)
    ? schedule.allowedPositions
    : Array.from({ length: shortestBlock }, (_, index) => index))
    .map(Number)
    .filter(Number.isInteger)
    .filter((position) => position >= 0 && position < shortestBlock)))
    .sort((a, b) => a - b);
  const effectivePositions = allowedPositions.length > 0 ? allowedPositions : [0];
  const preferredPosition = effectivePositions.includes(Number(schedule?.preferredPosition))
    ? Number(schedule.preferredPosition)
    : effectivePositions[0];

  return {
    mode,
    blocks: effectiveBlocks,
    allowedPositions: effectivePositions,
    preferredPosition,
    samePositionAcrossBlocks: schedule?.samePositionAcrossBlocks !== false,
    fixedRotations: normalizeRotationList(fixedRotations),
  };
}

export function getRequiredSchedule(config = {}) {
  return normalizeRequiredSchedule(config.requiredSchedule, config.fixedRotations);
}

export function getRequiredAssignmentCount(config = {}) {
  const schedule = getRequiredSchedule(config);
  if (schedule.mode === REQUIRED_SCHEDULE_MODE_PER_BLOCK) return schedule.blocks.length;
  return schedule.fixedRotations.length > 0 ? schedule.fixedRotations.length : 1;
}

export function getRequiredAllowedRotations(config = {}) {
  const schedule = getRequiredSchedule(config);
  if (schedule.mode === REQUIRED_SCHEDULE_MODE_FIXED) return schedule.fixedRotations;
  return Array.from(new Set(schedule.blocks.flatMap((block) => (
    schedule.allowedPositions.map((position) => block[position]).filter(Number.isInteger)
  )))).sort((a, b) => a - b);
}

function cartesianRotationPatterns(blocks, allowedPositions, index = 0, rotations = [], positions = []) {
  if (index >= blocks.length) return [{ rotations, positions }];
  return allowedPositions.flatMap((position) => {
    const rotation = blocks[index][position];
    if (!Number.isInteger(rotation)) return [];
    return cartesianRotationPatterns(
      blocks,
      allowedPositions,
      index + 1,
      [...rotations, rotation],
      [...positions, position]
    );
  });
}

export function getRequiredSchedulePatterns(config = {}) {
  const schedule = getRequiredSchedule(config);
  if (schedule.mode === REQUIRED_SCHEDULE_MODE_FIXED) {
    return schedule.fixedRotations.length > 0
      ? [{ rotations: schedule.fixedRotations.slice(), positions: [] }]
      : [];
  }
  const patterns = schedule.samePositionAcrossBlocks
    ? schedule.allowedPositions.map((position) => ({
      positions: schedule.blocks.map(() => position),
      rotations: schedule.blocks.map((block) => block[position]),
    }))
    : cartesianRotationPatterns(schedule.blocks, schedule.allowedPositions);
  return patterns
    .filter((pattern) => pattern.rotations.every(Number.isInteger))
    .sort((a, b) => {
      const preferredA = a.positions.filter((position) => position === schedule.preferredPosition).length;
      const preferredB = b.positions.filter((position) => position === schedule.preferredPosition).length;
      return preferredB - preferredA
        || a.positions.join(',').localeCompare(b.positions.join(','))
        || a.rotations.join(',').localeCompare(b.rotations.join(','));
    });
}

export function requiredScheduleIsSatisfied(config, assignedRotations = []) {
  const schedule = getRequiredSchedule(config);
  const assigned = new Set((assignedRotations || []).map(Number));
  if (schedule.mode === REQUIRED_SCHEDULE_MODE_FIXED) {
    if (schedule.fixedRotations.length === 0) return assigned.size >= 1;
    return schedule.fixedRotations.every((rotation) => assigned.has(rotation));
  }
  return getRequiredSchedulePatterns(config).some((pattern) => (
    pattern.rotations.every((rotation) => assigned.has(rotation))
  ));
}

export function getDefaultRequiredScheduleForSkill(name) {
  if (name !== 'ONE EIGHT' && name !== 'MASU MADNESS') {
    return normalizeRequiredSchedule({ mode: REQUIRED_SCHEDULE_MODE_FIXED }, []);
  }
  return normalizeRequiredSchedule({
    mode: REQUIRED_SCHEDULE_MODE_PER_BLOCK,
    blocks: DEFAULT_BLOCKS,
    allowedPositions: [0, 2],
    preferredPosition: name === 'ONE EIGHT' ? 0 : 2,
    samePositionAcrossBlocks: true,
  }, []);
}

